import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { Icon, Checkbox } from '@ant-design/react-native';
import { Button } from '@shared/components/md-design-system/Button/Button';
import { colorPalette } from '@shared/config/colorsConfig';
import { useTranslation } from 'react-i18next';
import { typography } from '@shared/config/typographyConfig';

interface FilterDropdownProps {
  label: string;
  selectedItems: string[];
  availableItems: string[];
  onItemsChange: (items: string[]) => void;
  onApplyFilter: () => void;
  onClearFilter: () => void;
  isFilterApplied: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  selectedItems,
  availableItems,
  onItemsChange,
  onApplyFilter,
  onClearFilter,
  isFilterApplied,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [stagedItems, setStagedItems] = useState<string[]>(selectedItems);

  const isAllSelected = stagedItems.length === availableItems.length;

  const handleItemToggle = (item: string) => {
    if (stagedItems.includes(item)) {
      setStagedItems(stagedItems.filter(i => i !== item));
    } else {
      setStagedItems([...stagedItems, item]);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setStagedItems([]);
    } else {
      setStagedItems([...availableItems]);
    }
  };

  const handleApply = () => {
    onItemsChange(stagedItems);
    onApplyFilter();
    setIsVisible(false);
  };

  const handleClear = () => {
    setStagedItems([]);
    onClearFilter();
    setIsVisible(false);
  };

  const handleCancel = () => {
    setStagedItems(selectedItems);
    setIsVisible(false);
  };

  const getButtonText = () => {
    if (selectedItems.length === 0) {
      return label;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return `${label} (${selectedItems.length})`;
  };

  return (
    <>
      <TouchableOpacity
        onPress={() => setIsVisible(true)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 16,
          paddingVertical: 8,
          backgroundColor: isFilterApplied ? colorPalette.primary[600] : colorPalette.white,
          borderRadius: 20,
          borderWidth: 1,
          borderColor: colorPalette.primary[600],
          minWidth: 80,
        }}
      >
        <Icon
          name="filter"
          size={16}
          color={isFilterApplied ? colorPalette.white : colorPalette.primary[600]}
          style={{ marginRight: 8 }}
        />
        <Text
          className="text-sm font-medium"
          style={{
            color: isFilterApplied ? colorPalette.white : colorPalette.primary[600],
          }}
        >
          {getButtonText()}
        </Text>
        {isFilterApplied && (
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={{ marginLeft: 8 }}
          >
            <Icon
              name="close"
              size={14}
              color={colorPalette.white}
            />
          </TouchableOpacity>
        )}
      </TouchableOpacity>

      <Modal
        visible={isVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCancel}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
          }}
          activeOpacity={1}
          onPress={handleCancel}
        >
          <TouchableOpacity
            activeOpacity={1}
            style={{
              backgroundColor: colorPalette.white,
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 300,
            }}
            onPress={() => {}}
          >
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}>
              <Text className="text-xl font-semibold text-black">
                {label}
              </Text>
              <TouchableOpacity onPress={handleCancel}>
                <Icon name="close" size={20} color={colorPalette.gray[500]} />
              </TouchableOpacity>
            </View>
            <View style={{ marginBottom: 16 }}>
              <TouchableOpacity
                onPress={handleSelectAll}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 12,
                  borderBottomWidth: 1,
                  borderBottomColor: colorPalette.gray[200],
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
                <Text className="ml-3 text-base text-black font-medium">
                  {t('buttons.selectAll')}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={{ 
              maxHeight: availableItems.length > 6 ? 300 : undefined
            }}>
              <ScrollView 
                showsVerticalScrollIndicator={availableItems.length > 6}
                style={{ flexGrow: 0 }}
              >
                {availableItems.map((item) => (
                  <TouchableOpacity
                    key={item}
                    onPress={() => handleItemToggle(item)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingVertical: 12,
                    }}
                  >
                    <Checkbox
                      checked={stagedItems.includes(item)}
                      onChange={() => handleItemToggle(item)}
                    />
                    <Text className="ml-3 text-base text-black">
                      {item}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <View style={{ 
              flexDirection: 'row', justifyContent: 'space-between',
              padding: 12,
              gap: 12 
            }}>
              <Button
                onPress={handleClear}
                variant="outline"
                style={{ flex: 1 }}
                textStyle={{ fontSize: typography.fontSize.sm }}
              >
                {t('buttons.remove')}
              </Button>
              <Button
                onPress={handleApply}
                variant="secondary"
                style={{ flex: 1 }}
                textStyle={{ fontSize: typography.fontSize.sm }}
              >
                {t('buttons.applyFilter')}
              </Button>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};
